import { useState } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';

// FRONT-2351: in-app replacement for the native window.prompt the code
// editor used for "New file" and "Rename". Validation mirrors the server's
// resolveBundlePath (src/apps/store.ts) so the user sees the problem before
// submit instead of a "Create failed" toast afterwards — and, since the
// files PUT is create-OR-overwrite, the "already exists" check is what stops
// "New file" from silently blanking an existing file.

export type FilePathDialogMode = { kind: 'new' } | { kind: 'rename'; from: string };

/** Folder of a path, with trailing slash; '' for the root. */
export function folderOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? '' : path.slice(0, i + 1);
}

/** Every folder in the tree ('' = root), sorted, for the folder picker. */
export function foldersOf(paths: string[]): string[] {
  const set = new Set<string>(['']);
  for (const p of paths) {
    const segs = p.split('/');
    for (let i = 1; i < segs.length; i++) set.add(segs.slice(0, i).join('/') + '/');
  }
  return [...set].sort();
}

/** Inline error for a target path, or null when it's fine to submit. */
export function validateFilePath(path: string, existing: string[]): string | null {
  if (!path) return 'Enter a file name.';
  if (path.startsWith('/')) return "The path can't start with '/'.";
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\\]/.test(path)) return 'The path contains invalid characters.';
  const segs = path.split('/');
  if (segs.includes('..')) return "The path can't contain '..'.";
  if (segs.some((s) => s === '' || s === '.')) return 'Each part of the path needs a name.';
  if (existing.includes(path)) return `${path} already exists.`;
  return null;
}

// Radix Select rejects '' as an item value, so the root gets a sentinel.
const ROOT = '/';

export function FilePathDialog({
  mode,
  existingPaths,
  defaultFolder,
  pending,
  serverError,
  onSubmit,
  onClose,
}: {
  /** null = closed. */
  mode: FilePathDialogMode | null;
  existingPaths: string[];
  defaultFolder: string;
  pending: boolean;
  serverError: string | null;
  onSubmit: (path: string) => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!mode} onOpenChange={(next) => !next && !pending && onClose()}>
      <DialogContent className="sm:max-w-md">
        {/* Keyed so the form's state resets each time the dialog opens. */}
        {mode && (
          <FilePathForm
            key={mode.kind === 'rename' ? `rename:${mode.from}` : 'new'}
            mode={mode}
            existingPaths={existingPaths}
            defaultFolder={defaultFolder}
            pending={pending}
            serverError={serverError}
            onSubmit={onSubmit}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FilePathForm({
  mode,
  existingPaths,
  defaultFolder,
  pending,
  serverError,
  onSubmit,
  onClose,
}: {
  mode: FilePathDialogMode;
  existingPaths: string[];
  defaultFolder: string;
  pending: boolean;
  serverError: string | null;
  onSubmit: (path: string) => void;
  onClose: () => void;
}) {
  const isRename = mode.kind === 'rename';
  const folders = foldersOf(existingPaths);
  const [folder, setFolder] = useState(isRename ? '' : defaultFolder);
  const [name, setName] = useState(isRename ? mode.from : '');
  const [touched, setTouched] = useState(false);

  const path = folder + name.trim();
  // A rename to the same path is a no-op, not an "already exists" error.
  const unchanged = isRename && path === mode.from;
  const error = unchanged ? null : validateFilePath(path, existingPaths);
  const canSubmit = !error && !unchanged && !pending;

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (canSubmit) onSubmit(path);
      }}
    >
      <DialogHeader>
        <DialogTitle>{isRename ? 'Rename file' : 'New file'}</DialogTitle>
        <DialogDescription>
          {isRename
            ? 'Change the name or move it to another folder with a path like assets/app.js.'
            : 'Creates an empty text file. Use / in the name to create a subfolder.'}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3">
        {!isRename && (
          <div className="space-y-1.5">
            <Label htmlFor="file-path-folder">Folder</Label>
            <Select
              value={folder || ROOT}
              onValueChange={(v) => setFolder(v === ROOT ? '' : v)}
              disabled={pending}
            >
              <SelectTrigger id="file-path-folder" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {folders.map((f) => (
                  <SelectItem key={f || ROOT} value={f || ROOT}>
                    {f || '/ (root)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="file-path-name">{isRename ? 'Path' : 'File name'}</Label>
          <Input
            id="file-path-name"
            autoFocus
            value={name}
            onChange={(e) => {
              setTouched(true);
              setName(e.target.value);
            }}
            placeholder={isRename ? 'assets/app.js' : 'app.js'}
            disabled={pending}
            aria-invalid={touched && !!error}
          />
          {touched && error && <p className="text-xs text-destructive">{error}</p>}
          {serverError && <p className="text-xs text-destructive">{serverError}</p>}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {pending && <CircleNotchIcon size={14} className="animate-spin" />}
          {isRename ? 'Rename' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}
